import type { ListingData } from "~/context/ListingPageDataContext";
import type {
  BookListingEstimate,
  BookListingQuote,
  IGuestRequestQuoteChild,
} from "~/types/api-types";
import { isMinimumSpendDepositListing } from "~/utils/pay-at-venue.utils";

import type { BookListingPriceSource } from "./BookListingPriceSummary.types";

import {
  fromPriceEstimate,
  fromQuote,
  PaymentPlans,
  Perspectives,
  Stages,
  type PriceFacts,
  type PriceView,
  type WireListing,
  type WireOfferQuote,
} from "@/components/molecules/PriceSummary";

type WireQuoteChild = NonNullable<WireOfferQuote["children"]>[number];

/**
 * Selects the most authoritative pricing source currently available.
 */
export const getBookListingPriceSource = ({
  estimate,
  quote,
}: {
  estimate?: BookListingEstimate;
  quote?: BookListingQuote;
}): BookListingPriceSource | null => {
  if (quote) {
    return { quote, type: "quote" };
  }

  if (estimate) {
    return { estimate, type: "estimate" };
  }

  return null;
};

/**
 * Maps listing-page data to the wire-neutral listing shape expected by the
 * design-system price readers.
 */
export const toWireListing = (listing: ListingData): WireListing => ({
  country: listing.listingCountry,
  full_service: listing.fullService,
  pay_at_venue: listing.payAtVenue,
});

const toWireQuoteChild = (
  contract: IGuestRequestQuoteChild,
): WireQuoteChild => {
  const wireContract = contract as unknown as WireQuoteChild;

  if (contract.type !== "SPACE_RENTAL") {
    return wireContract;
  }

  return {
    ...wireContract,
    meta: {
      ...wireContract.meta,
      booking: {
        ...wireContract.meta?.booking,
        dts_start: contract.meta.booking.dts_start,
        discounts: contract.meta.booking.discounts,
        duration: contract.meta.booking.duration,
        rate: contract.meta.booking.rate,
      },
    },
    type: contract.type,
  };
};

/**
 * Maps the listing quote context shape back to the quote wire shape consumed
 * by the design-system `fromQuote` reader.
 */
export const toWireOfferQuote = (quote: BookListingQuote): WireOfferQuote => {
  const calculations = quote.masterContract?.calculations;
  const wireCalculations = calculations as
    | (typeof calculations & {
        gratuity_amount?: number;
        gratuity_rate?: number;
      })
    | undefined;

  return {
    children: [
      ...(quote.spaceRentalContract
        ? [toWireQuoteChild(quote.spaceRentalContract)]
        : []),
      ...quote.addOnsContracts.map(toWireQuoteChild),
    ],
    master: quote.masterContract
      ? ({
          ...quote.masterContract,
          calculations: calculations
            ? {
                ...wireCalculations,
                gratuity_amount:
                  calculations.guest.gratuity_amount ??
                  wireCalculations?.gratuity_amount,
                gratuity_rate:
                  calculations.guest.gratuity_rate ??
                  wireCalculations?.gratuity_rate,
                // The DS price summary prefers the guest.platform_fee_amount over the guest.fee,
                // For US listings the platform_fee_amount = fee,
                // But UK listings the fee includes the VAT tax.
                // So we need to use the platform_fee_amount if it exists, otherwise use the fee.
                guest: {
                  ...wireCalculations?.guest,
                  platform_fee_amount:
                    calculations?.guest?.fee ??
                    wireCalculations?.guest?.fee ??
                    wireCalculations?.guest?.platform_fee_amount,
                },
              }
            : undefined,
        } as WireOfferQuote["master"])
      : undefined,
  };
};

/**
 * Builds the guest pre-checkout view used by the listing booking panel.
 *
 * The guest has not chosen a payment plan yet at this stage, so eligibility
 * flags such as zero-down or split payment must not select a schedule here.
 * Deposit listings hide DS Section 3 (Due today / Due at venue) because the
 * listing fee-calc shows `FsvPaymentSchedule` instead.
 */
export const buildBookListingPriceView = ({
  listing,
  quote,
}: {
  listing: ListingData;
  quote?: BookListingQuote;
}): PriceView => {
  const hasPayAtVenueCalculations =
    quote?.masterContract?.calculations.guest.pay_at_venue != null;
  const isDepositListing = isMinimumSpendDepositListing(listing.payAtVenue);

  const paymentPlan =
    isDepositListing || hasPayAtVenueCalculations
      ? PaymentPlans.PayAtVenue
      : PaymentPlans.PayInFull;

  return {
    paymentPlan,
    perspective: Perspectives.Guest,
    stage: Stages.PreCheckout,
    ...(isDepositListing ? { suppressGuestPaymentSchedule: true } : {}),
  };
};

/**
 * Reads the selected listing pricing source into normalized design-system
 * price facts.
 */
export const getBookListingPriceFacts = ({
  listing,
  source,
  view,
}: {
  listing: ListingData;
  source: BookListingPriceSource;
  view: PriceView;
}): PriceFacts => {
  const wireListing = toWireListing(listing);

  switch (source.type) {
    case "estimate":
      return fromPriceEstimate({
        estimate: source.estimate,
        listing: wireListing,
      });
    case "quote":
      return fromQuote({
        disableSalesTax: true,
        listing: wireListing,
        quote: toWireOfferQuote(source.quote),
        view,
      });
    default: {
      const exhaustiveSource: never = source;
      return exhaustiveSource;
    }
  }
};

/**
 * `nest_processing_fee_test` treatment: hides cleaning/processing rows and,
 * when the guest is bucketed into the blended pricing bucket, uses that wire amount for
 * hourly rate and space rental. `bookingTotal` is left untouched — it already
 * reflects the true payment. Dateless and FSV facts are left unchanged.
 * Moving forward, this is the pattern we should for experiment and temp changes
 * vs updating the price summary directly.
 * TODO: If experiment is successful, we will need to remove this
 * function and update the price summary directly.
 */
export const toNestedFeePriceFacts = (
  facts: PriceFacts,
  quote: BookListingQuote | undefined,
): PriceFacts => {
  if (!facts.isDiy || !facts.datesKnown) return facts;

  const blended = quote?.masterContract?.calculations?.blended_pricing_guest;

  return {
    ...facts,
    cleaningFee: null,
    processingFee: null,
    originalProcessingFee: null,
    hourlyRate:
      typeof blended?.hourly === "number" ? blended.hourly : facts.hourlyRate,
    spaceRental:
      typeof blended?.total === "number" ? blended.total : facts.spaceRental,
  };
};
